import React, { useEffect, useState, useDeferredValue, useRef } from 'react';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import { useAuth } from '../../context/authContext';
import { ordenarFranjas } from '../../utils/franjas';
import AuthImage, { descargarFicheroProtegido } from "../../components/AuthImage";
import BotonVolver from "../../components/BotonVolver";

const estadoColores = { pedido: '#EE82EE', comprado: '#3b82f6', 'en reparto': '#f59e0b', entregado: '#10b981', 'en la puerta': '#d12b39ff' };
const estadoOrder = ['pedido','comprado','en reparto','entregado','en la puerta'];

const inputStyle = { padding:'8px 12px', fontSize:14, height:40, border:'1px solid #ccc', borderRadius:4, outline:'none', minWidth:140 };
// Variantes para controlar ancho/longitud de filtros
const inputStyleWide = { ...inputStyle, minWidth: 120, flex: '0 0 10px', height:25 }; // para campos como Abreviatura
const inputStyleDate = { ...inputStyle, width: 120, minWidth: 120, flex: '0 0 10px', height:25 }; // para inputs tipo date

function normalize(str){ return (str||'').toString().normalize('NFD').replace(/\p{Diacritic}/gu,'').toLowerCase().trim(); }

const VerComprasComprador = () => {
  const { user } = useAuth();
  // Identificador usuario
  let loginRaw = '';
  if (user && typeof user === 'object') {
    for (const k of ['user','usuario','username','nombre_usuario','nombre','correo','email']) {
      if (user[k] && typeof user[k] === 'string' && user[k].trim()) { loginRaw = user[k].trim(); break; }
    }
  }
  const loginNorm = normalize(loginRaw);
  // Nuevo criterio: el usuario 'Javier' ve TODAS las compras (antes se usaba tipo==='comprador_jefe')
  const esJavier = loginRaw.toLowerCase() === 'javier';

  // Estado datos paginados
  const [items, setItems] = useState([]); // compras de la página actual
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = 50; // fijo

  // Filtros
  const [filtroAbreviatura, setFiltroAbreviatura] = useState('');
  const [filtroEstado, setFiltroEstado] = useState(''); // vacío = todos (para alinearlo con admin)
  const [filtroRuta, setFiltroRuta] = useState('');
  const [filtroFranja, setFiltroFranja] = useState('');
  // Nuevo: Javier puede filtrar por cualquier comprador explícitamente
  const [filtroComprador, setFiltroComprador] = useState('');
  const [filtroTicket, setFiltroTicket] = useState('');
  const [fechaPedido, setFechaPedido] = useState('');
  const [fechaCompra, setFechaCompra] = useState('');
  const defAbrev = useDeferredValue(filtroAbreviatura);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mostrarFiltrosMovil, setMostrarFiltrosMovil] = useState(false); // oculto por defecto

  // Ticket UI / upload states
  const fileInputRef = useRef(null);
  const [ticketsVisibles, setTicketsVisibles] = useState({}); // { [compraId]: true }
  const [uploadTargetId, setUploadTargetId] = useState(null);
  const [uploadingForId, setUploadingForId] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [uploadForm, setUploadForm] = useState(null); // { iva_4, iva_10, iva_21, precio_comprador, file, error }
  // preview URLs for tickets (either original server URL or temporary blob URL when user selects a new photo)
  const [previewTickets, setPreviewTickets] = useState({}); // { [compraId]: url }
  const previewOriginalsRef = useRef({}); // store original server URLs to restore on cancel
  const createdPreviewUrlsRef = useRef({}); // track created blob URLs for revocation
  const [helpVisible, setHelpVisible] = useState(false); // show help overlay with example photo + description
  const [helpImageVisible, setHelpImageVisible] = useState(false); // show enlarged image modal
  const helpImagePushedRef = useRef(false);

  

  const helpPushedRef = useRef(false);
  const modalPushedRef = useRef(false);
  // unified popstate handler: close help overlays when back is pressed
  useEffect(() => {
    const onPop = (e) => {
      if (helpImagePushedRef.current) {
        helpImagePushedRef.current = false;
        setHelpImageVisible(false);
        return;
      }
      if (helpPushedRef.current) {
        helpPushedRef.current = false;
        setHelpVisible(false);
        return;
      }
      if (modalPushedRef.current) {
        modalPushedRef.current = false;
        setUploadTargetId(null);
        setUploadForm(null);
        return;
      }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const openHelp = () => {
    try {
      if (!helpPushedRef.current) {
        window.history.pushState({ help: true }, '');
        helpPushedRef.current = true;
      }
    } catch (_) {}
    setHelpVisible(true);
  };

  const openUploadModal = (compra) => {
    const pre = {
      iva_4: compra?.iva_4 != null ? String(compra.iva_4) : '',
      iva_10: compra?.iva_10 != null ? String(compra.iva_10) : '',
      iva_21: compra?.iva_21 != null ? String(compra.iva_21) : '',
      precio_comprador: compra?.precio_comprador != null ? String(compra.precio_comprador) : '',
      file: null,
      error: null,
    };
    // push history so Back closes the modal first
    try {
      if (!modalPushedRef.current) {
        window.history.pushState({ uploadModal: true }, '');
        modalPushedRef.current = true;
      }
    } catch (_) {}
    setUploadTargetId(compra.id);
    setUploadForm(pre);
    // ensure main ticket view is visible and preload existing image into preview
    const orig = compra?.ticket ? `/api/tickets/${compra.ticket}` : null;
    previewOriginalsRef.current[compra.id] = orig;
    setPreviewTickets(prev => ({ ...prev, [compra.id]: orig }));
    setTicketsVisibles(prev => ({ ...prev, [compra.id]: true }));
  };

  // Franjas desde backend (distinct pisos)
  const [franjasDisponibles, setFranjasDisponibles] = useState([]);
  const [compradoresLista, setCompradoresLista] = useState([]); // solo para Javier
  useEffect(()=>{
    const loadFranjas = async () => {
      try {        
        const r = await axios.get(`/api/pisos/franjas-distinct`);
        if (Array.isArray(r.data)) setFranjasDisponibles(ordenarFranjas(r.data));
      } catch(e){ console.warn('[VerComprasComprador] No franjas', e); }
    };
    loadFranjas();
  }, []);

  // Si es Javier, cargar lista de compradores para desplegable
  useEffect(()=>{
    if(!esJavier) return;
    const fetchCompradores = async () => {
      try {        
        const url = `/api/pisos/usuarios-por-tipo`;
        const res = await axios.get(url);
        const lista = Array.isArray(res.data?.compradores) ? res.data.compradores.map(u=>u.user).filter(Boolean) : [];
        setCompradoresLista(lista.sort((a,b)=> a.localeCompare(b, 'es', {sensitivity:'base'})));
      } catch(e){ console.warn('[VerComprasComprador] Error cargando compradores', e); }
    };
    fetchCompradores();
  }, [esJavier]);

  // Obtener rutas dinámicas (derivadas de items recibidos hasta la página actual)
  const rutasDisponibles = [...new Set(items.map(c=>c.ruta_piso).filter(Boolean))].sort();

  const formatearFecha = (f) => {
    if(!f) return ''; const iso=f.replace(' ','T'); const d=new Date(iso); if(isNaN(d)) return f; const dd=String(d.getDate()).padStart(2,'0'); const mm=String(d.getMonth()+1).padStart(2,'0'); const yyyy=d.getFullYear(); const hh=String(d.getHours()).padStart(2,'0'); const mi=String(d.getMinutes()).padStart(2,'0'); return `${dd}-${mm}-${yyyy} ${hh}:${mi}`;
  };

  const fetchPage = async (targetPage=page, keepScroll=false) => {
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams();
      params.set('page', targetPage); params.set('pageSize', pageSize);
      if (defAbrev) params.set('abreviatura', defAbrev);
      if (filtroEstado) params.set('estado', filtroEstado);
      if (filtroRuta) params.set('ruta', filtroRuta);
      if (filtroFranja) params.set('franja', filtroFranja);
      if (fechaPedido){ params.set('diaPedidoInicio', fechaPedido); params.set('diaPedidoFin', fechaPedido); }
      if (fechaCompra){ params.set('diaCompraInicio', fechaCompra); params.set('diaCompraFin', fechaCompra); }
      if (filtroTicket) params.set('ticket', filtroTicket);
      // Ahora sí: mandamos el comprador al backend para que él filtre y devuelva total correcto
      if (esJavier) {
        if (filtroComprador.trim()) {
          params.set('comprador', filtroComprador.trim());
        }
      } else if (loginRaw) {
        params.set('comprador', loginRaw);
      }
      const url = `/api/admin/verCompras/compras?${params.toString()}`;
      // console.log('[VerComprasComprador] Fetch URL:', url);
      const res = await axios.get(url);
      const arr = Array.isArray(res.data?.items)? res.data.items:[];
      // console.log('[VerComprasComprador] page', targetPage, 'items recibidos:', arr.length, 'total backend:', res.data?.total, 'bypassComprador=', esJavier);
      setItems(arr);
      setTotal(Number(res.data?.total||0));
      setPage(Number(res.data?.page||targetPage));
      if(!keepScroll) window.scrollTo({top:0});
    } catch(e){
      console.error('[VerComprasComprador] Error fetch', e);
      setError('❌ Error al cargar las compras'); setItems([]); setTotal(0);
    } finally { setLoading(false); setInitialLoading(false); }
  };

  useEffect(()=>{ fetchPage(1); /* inicial */ }, []); // eslint-disable-line
  useEffect(()=>{ const t=setTimeout(()=> { setPage(1); fetchPage(1); },150); return ()=> clearTimeout(t); }, [defAbrev, filtroEstado, filtroRuta, filtroFranja, fechaPedido, fechaCompra, esJavier, loginNorm, filtroComprador, filtroTicket]);

  // cleanup any created blob URLs on unmount
  useEffect(()=>{
    return () => {
      try {
        const map = createdPreviewUrlsRef.current || {};
        Object.values(map).forEach(u => { try { URL.revokeObjectURL(u); } catch(_){} });
        createdPreviewUrlsRef.current = {};
      } catch(_){ }
    };
  }, []);

  const descargarCompraPDF = (compra) => {
    try {
      const pageHeight = 297; // A4 en mm
      const marginTop = 20;
      const lineHeight = 10;
      const bottomMargin = 20;
      const maxY = pageHeight - bottomMargin;

      const doc = new jsPDF();
      doc.setFontSize(18);
      let y = marginTop;
      doc.setFont('helvetica','bold');

      let abreviatura_piso_sin_cajetin = (compra.abreviatura_piso || '').split("(")[0];

      const info_piso = `Piso ID: ${compra.id_piso} - ${abreviatura_piso_sin_cajetin}`;
      doc.text(info_piso, 10, y); y += 15;

      doc.setFontSize(14);
      const fechaPedido = compra.dia_pedido || '';
      doc.text(`Fecha pedido: ${fechaPedido}`, 10, y); y += lineHeight;
      doc.text(`Franja horaria: ${compra.franja_horaria || '-'}`, 10, y); y += lineHeight;
      if (compra.user_supervisora) { doc.text(`Supervisora: ${compra.user_supervisora}`, 10, y); y += lineHeight; }

      if (compra.notas_supervisora && String(compra.notas_supervisora).trim() !== '') {
        y += 4;
        doc.text('Notas de la supervisora:', 10, y);
        y += 8;
        const maxWidth = 180;
        const paddingH = 5;
        const paddingW = 5;
        const notasWrapped = doc.splitTextToSize(String(compra.notas_supervisora), maxWidth - paddingW*2);
        const blockHeight = notasWrapped.length * lineHeight + paddingH * 2;
        if (y + blockHeight > maxY) { doc.addPage(); y = marginTop; }
        doc.rect(10, y, maxWidth, blockHeight);
        doc.setFont('helvetica','normal');
        let textY = y + paddingH + 2;
        notasWrapped.forEach(linea => { if (textY > maxY) { doc.addPage(); textY = marginTop; } doc.text(linea, 10 + paddingW, textY); textY += lineHeight; });
        y += blockHeight + 6;
      } else {
        doc.setFont('helvetica','normal');
      }

      y += lineHeight;
      (compra.productos || []).slice().sort((a,b)=> (a.nombre||'').localeCompare(b.nombre||''))
        .forEach(p => { if (y > maxY) { doc.addPage(); y = marginTop; } doc.text(`- ${p.nombre} (Cantidad: ${p.cantidad})`, 10, y); y += lineHeight; });

      const fechaFormateada = String(fechaPedido).replace(/\//g,'-').replace(/[: ]/g,'_');
      doc.save(`Compra_${compra.id}_${compra.abreviatura_piso}_${fechaFormateada}.pdf`);
    } catch (e) { alert('No se pudo generar el PDF de la compra.'); }
  };

  // Compress image before upload to avoid large payloads (mobile cameras)
  const compressImage = (file, maxWidth = 1280, quality = 0.7) => {
    return new Promise((resolve, reject) => {
      if (!file) return reject(new Error('No file'));
      const reader = new FileReader();
      reader.onload = function(e) {
        const img = new Image();
        img.onload = function() {
          const canvas = document.createElement('canvas');
          const scale = Math.min(1, maxWidth / img.width);
          canvas.width = Math.round(img.width * scale);
          canvas.height = Math.round(img.height * scale);
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          canvas.toBlob(blob => {
            if (!blob) return reject(new Error('Compression failed'));
            const newFile = new File([blob], (file.name || 'ticket.jpg').replace(/\s+/g,'_'), { type: 'image/jpeg' });
            resolve(newFile);
          }, 'image/jpeg', quality);
        };
        img.onerror = reject;
        img.src = e.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const subirTicket = async (compraId, file) => {
    setUploadError(null);
    if (!compraId || !file) {
      setUploadError('Faltan id o archivo');
      return;
    }
    try {
      setUploadingForId(compraId);
      // compress
      let toUpload = file;
      try {
        toUpload = await compressImage(file, 1280, 0.7);
      } catch(err){
        // ignore compression failure, use original
        console.warn('Compress failed, using original', err);
        toUpload = file;
      }

      const fd = new FormData();
      fd.append('id', String(compraId));
      fd.append('ticket', toUpload, toUpload.name || `ticket_${compraId}.jpg`);

      const res = await axios.post('/api/compras/subir_ticket', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 20000,
      });
      if (res?.data?.ok) {
        // refresh current page
        await fetchPage(page, true);
        setTicketsVisibles(prev => ({ ...prev, [compraId]: true }));
      } else {
        setUploadError(res?.data?.error || 'Error al subir');
      }
    } catch (e) {
      console.error('upload error', e);
      setUploadError(e?.response?.data?.error || e.message || 'Error al subir');
    } finally {
      setUploadingForId(null);
      // clear file input
      try { if (fileInputRef.current) fileInputRef.current.value = ''; } catch(_){}
      try { if (modalPushedRef.current) { window.history.back(); } else { setUploadTargetId(null); } } catch(_) { setUploadTargetId(null); }
    }
  };

  const borrarTicket = async (compraId) => {
    if (!compraId) return;
    const ok = window.confirm('¿Eliminar el ticket de esta compra? Esta acción no se puede deshacer.');
    if (!ok) return;
    try {
      setUploadingForId(compraId);
      const res = await axios.post('/api/compras/borrar_ticket', { id: String(compraId) });
      if (res?.data?.ok) {
          // cleanup any temp preview for this compra
          try { const tmp = createdPreviewUrlsRef.current[compraId]; if (tmp) { URL.revokeObjectURL(tmp); delete createdPreviewUrlsRef.current[compraId]; } } catch(_){ }
          previewOriginalsRef.current[compraId] = null;
          setPreviewTickets(prev => ({ ...(prev||{}), [compraId]: null }));
          await fetchPage(page, true);
          setTicketsVisibles(prev => ({ ...prev, [compraId]: false }));
      } else {
        alert(res?.data?.error || 'No se pudo borrar el ticket');
      }
    } catch (e) {
      console.error('borrarTicket error', e);
      alert(e?.response?.data?.error || e.message || 'Error al borrar ticket');
    } finally {
      setUploadingForId(null);
    }
  };

  if (initialLoading) return <p>Cargando compras...</p>;
  if (error) return <p>{error}</p>;

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const goToPage = (p) => { const np = Math.min(Math.max(1,p), totalPages); if(np!==page){ fetchPage(np); } };
  // Simplificado: si >5 páginas -> [1,2,3,'…', totalPages]; si <=5 mostrar todas
  const pageWindow = totalPages <= 5 ? Array.from({length: totalPages}, (_,i)=> i+1) : [1,2,3,'…', totalPages];

  return (
    <div style={{ maxWidth:'1100px', margin:'0 auto', padding:'34px 20px 20px', paddingBottom: '110px', fontFamily:'Arial, sans-serif', position:'relative' }}>
      <style>{`details summary::-webkit-details-marker { display: none; } details summary::marker { display: none; } details summary { position: relative; } details.cambios-details summary::before { content: '▶'; position: absolute; left: 0; transition: transform 0.2s; font-size: 11px; } details.cambios-details[open] summary::before { transform: rotate(90deg); }`}</style>
      <BotonVolver fallback="/comprador" />
      {/* Botón inline centrado mostrar/ocultar filtros */}
      <div style={{ display:'flex', justifyContent:'center', marginBottom: mostrarFiltrosMovil ? 16 : 18 }}>
        <button
          onClick={()=> setMostrarFiltrosMovil(p=>!p)}
          aria-pressed={mostrarFiltrosMovil}
          style={{
            padding:'10px 24px', fontSize:16, background:'#b294d6', color:'#fff', border:'none', borderRadius:10,
            cursor:'pointer', fontWeight:600, boxShadow:'0 4px 12px rgba(0,0,0,0.20)', letterSpacing:'.5px'
          }}
        >{mostrarFiltrosMovil ? '❌ Ocultar filtros' : '🔍 Mostrar filtros'}</button>
      </div>

      <h2 style={{ textAlign:'center', fontSize:26, marginBottom:14 }}>Histórico de Compras</h2>

  <div id='filtros-comprador-container' style={{ display: mostrarFiltrosMovil ? 'flex':'none', flexWrap:'wrap', gap:10, marginBottom:20, alignItems:'center', justifyContent:'center'}}>
  <input style={inputStyleWide} placeholder='Abreviatura' value={filtroAbreviatura} onChange={e=>setFiltroAbreviatura(e.target.value)} />
        <select style={inputStyle} value={filtroEstado} onChange={e=>setFiltroEstado(e.target.value)}>
          <option value=''>Todos estados</option>
          {estadoOrder.map(est=> <option key={est} value={est}>{est}</option>)}
        </select>
        <select style={inputStyle} value={filtroRuta} onChange={e=>setFiltroRuta(e.target.value)}>
          <option value=''>Todas rutas</option>
          {rutasDisponibles.map(r=> <option key={r} value={r}>{r}</option>)}
        </select>
        <select style={inputStyle} value={filtroFranja} onChange={e=>setFiltroFranja(e.target.value)}>
          <option value=''>Todas franjas</option>
          {franjasDisponibles.map(f=> <option key={f} value={f}>{f}</option>)}
        </select>
        {esJavier && (
          <select style={inputStyle} value={filtroComprador} onChange={e=>setFiltroComprador(e.target.value)}>
            <option value=''>Todos compradores</option>
            {compradoresLista.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        )}
        <select
          style={{
            ...inputStyle,
            background: filtroTicket === 'with' ? '#bbf7d0' : filtroTicket === 'without' ? '#fecaca' : undefined,
            fontWeight: filtroTicket ? 700 : undefined,
          }}
          value={filtroTicket}
          onChange={e=>setFiltroTicket(e.target.value)}
        >
          <option value=''>Todos los tickets</option>
          <option value='with'>Ticket subido</option>
          <option value='without'>Ticket pendiente</option>
        </select>
        <div style={{ display:'flex', flexDirection:'column', gap:4}}>
          <span style={{ fontSize:12, fontWeight:700 }}>Día Pedido</span>
          <input type='date' style={inputStyleDate} value={fechaPedido} onChange={e=>setFechaPedido(e.target.value)} />
        </div>
        <div style={{ display:'flex', flexDirection:'column', gap:4}}>
          <span style={{ fontSize:12, fontWeight:700 }}>Día Compra</span>
          <input type='date' style={inputStyleDate} value={fechaCompra} onChange={e=>setFechaCompra(e.target.value)} />
        </div>
        <button
          onClick={()=>{ setFiltroAbreviatura(''); setFiltroEstado(''); setFiltroRuta(''); setFiltroFranja(''); setFiltroComprador(''); setFiltroTicket(''); setFechaPedido(''); setFechaCompra(''); fetchPage(1); }}
          style={{ ...inputStyle, cursor:'pointer', background:'#fff', fontWeight:700, border:'4px solid #000', borderRadius:'999px', padding:'10px 18px', boxShadow:'0 4px 0 #000', height:'auto' }}
        >🧹 Limpiar</button>
      </div>

      {/* hidden global file input used for subir ticket */}
      <input
        ref={fileInputRef}
        type='file'
        accept='image/*'
        capture='environment'
        style={{ display: 'none' }}
        onChange={async (e) => {
          const f = e.target.files && e.target.files[0];
          if (!f) return;
          const id = uploadTargetId;
          if (!id) return;
          // legacy path: if this hidden input is used, just upload with default behavior (no IVA fields)
          await subirTicket(id, f);
        }}
      />

      {/* Upload / Edit modal */}
      {uploadTargetId && uploadForm && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 12000, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.5)' }}>
          <div style={{ width: 'min(720px, 96vw)', background: '#fff', borderRadius: 12, padding: 18 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
              <h3 style={{ marginTop:0 }}>Subir / Editar ticket</h3>
              <button
                title='Mostrar ayuda: ejemplo de ticket y qué valores introducir'
                onClick={openHelp}
                style={{ border: 'none', background: '#f8e80bff', color: '#3730a3', padding: '6px 10px', borderRadius: 999, cursor: 'pointer', fontSize: 16 }}
              >
                ℹ️
              </button>
            </div>
            <p style={{ marginTop: 0, color: '#555' }}>Rellena los campos IVA y el Precio total.</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap:6 }}>
                IVA 4%
                <input type='number' step='0.01' min='0' value={(uploadForm?.iva_4 ?? '')} onChange={e=> setUploadForm(f=> ({ ...(f||{}), iva_4: e.target.value }))} style={{ padding:8 }} />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap:6 }}>
                IVA 10%
                <input type='number' step='0.01' min='0' value={(uploadForm?.iva_10 ?? '')} onChange={e=> setUploadForm(f=> ({ ...(f||{}), iva_10: e.target.value }))} style={{ padding:8 }} />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap:6 }}>
                IVA 21%
                <input type='number' step='0.01' min='0' value={(uploadForm?.iva_21 ?? '')} onChange={e=> setUploadForm(f=> ({ ...(f||{}), iva_21: e.target.value }))} style={{ padding:8 }} />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap:6 }}>
                Precio total
                <input type='number' step='0.01' min='0' value={(uploadForm?.precio_comprador ?? '')} onChange={e=> setUploadForm(f=> ({ ...(f||{}), precio_comprador: e.target.value }))} style={{ padding:8 }} />
              </label>
            </div>
            {/* preview inside modal: show current or newly selected image */}
            {uploadTargetId && (previewTickets[uploadTargetId] || previewOriginalsRef.current[uploadTargetId]) && (
              <div style={{ marginTop: 10, display: 'flex', justifyContent: 'center' }}>
                <AuthImage src={previewTickets[uploadTargetId] || previewOriginalsRef.current[uploadTargetId]} alt='preview' style={{ maxWidth: '320px', maxHeight: '220px', objectFit: 'contain', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.12)' }} />
              </div>
            )}
            <div style={{ marginTop:12 }}>
              <label style={{ display:'flex', flexDirection:'column', gap:6 }}>
                Foto del ticket
                <input type='file' accept='image/*' capture='environment' onChange={e=> {
                  const file = e.target.files && e.target.files[0];
                  const cid = uploadTargetId;
                  setUploadForm(f=> ({ ...(f||{}), file }));
                  if (!cid) return;
                  // revoke previous created blob for this compra if any
                  try {
                    const prev = createdPreviewUrlsRef.current[cid];
                    if (prev) { URL.revokeObjectURL(prev); delete createdPreviewUrlsRef.current[cid]; }
                  } catch(_){ }
                  if (file) {
                    const blobUrl = URL.createObjectURL(file);
                    createdPreviewUrlsRef.current[cid] = blobUrl;
                    setPreviewTickets(prev=> ({ ...(prev||{}), [cid]: blobUrl }));
                    // also make sure main ticket area visible
                    setTicketsVisibles(prev => ({ ...prev, [cid]: true }));
                  }
                }} />
              </label>
            </div>
            {uploadForm?.error && <div style={{ color: 'red', marginTop:8, fontWeight:700 }}>{uploadForm.error}</div>}
            <div style={{ display:'flex', justifyContent:'flex-end', gap:8, marginTop:14 }}>
              <button onClick={()=> {
                // revert any temporary preview and cleanup
                const cid = uploadTargetId;
                try {
                  const tmp = createdPreviewUrlsRef.current[cid];
                  if (tmp) { URL.revokeObjectURL(tmp); delete createdPreviewUrlsRef.current[cid]; }
                } catch(_){ }
                // restore original preview
                const orig = previewOriginalsRef.current[cid] || null;
                setPreviewTickets(prev => ({ ...(prev||{}), [cid]: orig }));
                // close modal: if we pushed history, go back to remove entry; otherwise just close
                try {
                  if (modalPushedRef.current) { window.history.back(); return; }
                } catch(_) {}
                setUploadTargetId(null);
                setUploadForm(null);
              }} style={{ padding:'8px 12px' }}>Cancelar</button>
              <button onClick={async ()=> {
                // validate
                const f = uploadForm || {};
                const required = ['iva_4','iva_10','iva_21','precio_comprador','file'];
                for (const k of required) {
                  if (f[k] === undefined || f[k] === null || f[k] === '') {
                    setUploadForm(prev=> ({ ...(prev||{}), error: 'Todos los campos son obligatorios'}));
                    return;
                  }
                }
                const iva4 = parseFloat(String(f.iva_4).replace(',','.')) || 0;
                const iva10 = parseFloat(String(f.iva_10).replace(',','.')) || 0;
                const iva21 = parseFloat(String(f.iva_21).replace(',','.')) || 0;
                const precio = parseFloat(String(f.precio_comprador).replace(',','.')) || 0;
                const sum = +(iva4 + iva10 + iva21).toFixed(2);
                const eps = 0.01;
                if (Math.abs(sum - precio) > eps) {
                  setUploadForm(prev=> ({ ...(prev||{}), error: `La suma de IVA debe ser igual al Precio Total, comprueba los datos introducidos.`}));
                  return;
                }
                // proceed to upload
                try {
                  setUploadingForId(uploadTargetId);
                  // compress
                  let toUpload = f.file;
                  try { toUpload = await compressImage(f.file, 1280, 0.7); } catch(_) { toUpload = f.file; }
                  const fd = new FormData();
                  fd.append('id', String(uploadTargetId));
                  fd.append('ticket', toUpload, toUpload.name || `ticket_${uploadTargetId}.jpg`);
                  fd.append('iva_4', String(iva4));
                  fd.append('iva_10', String(iva10));
                  fd.append('iva_21', String(iva21));
                  fd.append('precio_comprador', String(precio));
                  const res = await axios.post('/api/compras/subir_ticket', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 20000 });
                  if (res?.data?.ok) {
                    // cleanup created blob URL if any and replace preview with server file
                    const cid = uploadTargetId;
                    try {
                      const tmp = createdPreviewUrlsRef.current[cid];
                      if (tmp) { URL.revokeObjectURL(tmp); delete createdPreviewUrlsRef.current[cid]; }
                    } catch(_){ }
                    if (res.data?.ticket) {
                      setPreviewTickets(prev => ({ ...(prev||{}), [cid]: `/api/tickets/${res.data.ticket}`}));
                    }
                    await fetchPage(page, true);
                    setTicketsVisibles(prev => ({ ...prev, [uploadTargetId]: true }));
                    // close modal: prefer history.back() if we pushed state
                    try { if (modalPushedRef.current) { window.history.back(); } else { setUploadTargetId(null); setUploadForm(null); } } catch(_) { setUploadTargetId(null); setUploadForm(null); }
                  } else {
                    setUploadForm(prev=> ({ ...(prev||{}), error: res?.data?.error || 'Error al subir' }));
                  }
                } catch (e) {
                  console.error('upload error', e);
                  setUploadForm(prev=> ({ ...(prev||{}), error: e?.response?.data?.error || e.message || 'Error al subir' }));
                } finally { setUploadingForId(null); }
              }} style={{ padding:'8px 12px', background:'#16a34a', color:'#fff', border:'none', borderRadius:8 }}>Confirmar</button>
            </div>
          </div>
        </div>
      )}

      <div style={{ textAlign:'center', margin:'8px 0 18px' }}>
        <span style={{ background:'#f0f9ff', border:'2px solid #93c5fd', padding:'8px 14px', borderRadius:14, fontWeight:800, color:'#1e3a8a' }}>Total: {total}</span>
      </div>

      {items.length===0 && !loading && <p style={{ textAlign:'center', marginTop:30 }}>No hay compras para mostrar.</p>}

      <div>
        {items.map(compra => {
          const estadoColor = estadoColores[compra.estado] || '#555';
          const isPedido = compra.estado === 'pedido';
          return (
            <div key={compra.id} style={{ border:'1px solid #ddd', borderLeft:`6px solid ${estadoColor}`, borderRadius:10, padding:'12px 14px', marginBottom:10, background: isPedido ? '#e6f4ff' : (compra.ticket ? '#c6f6d5' : '#ffd6d6'), boxShadow:'0 2px 6px rgba(0,0,0,0.06)' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:8, marginBottom:6 }}>
                <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap', minWidth:220 }}>
                  <span style={{ fontWeight:700, fontSize:18, color:'#1976d2' }}>{compra.abreviatura_piso}</span>
                  {compra.ruta_piso && <span style={{ background:'#eef7ff', color:'#1e3a8a', padding:'2px 8px', borderRadius:12, fontSize:12, fontWeight:600, border:'1px solid #93c5fd' }}>Ruta: {compra.ruta_piso}</span>}
                  <span style={{ fontWeight:800, color:estadoColor, textTransform:'capitalize', border:`2px solid ${estadoColor}`, padding:'4px 10px', borderRadius:14, fontSize:13, background:'#f7f7f7' }}>{compra.estado}</span>
                  {compra.franja_horaria && <span style={{ background:'#eafbea', color:'#256029', padding:'2px 8px', borderRadius:12, fontSize:12, fontWeight:600, border:'1px solid #7fc27f' }}>Franja: {compra.franja_horaria}</span>}
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                  <span style={{ fontWeight:800, fontSize:16, color:'#388e3c' }}>
                    {Number(compra.precio_final||0).toFixed(2)} €
                    {compra.precio_real != null && (" → ")}
                    {compra.precio_real != null && <span style={{ color:'#0b6623' }}>{Number(compra.precio_real).toFixed(2)} €</span>}
                  </span>
                  {(compra.n_bolsas!=null) && <span style={{ fontWeight:800, fontSize:16, color:'#333' }}>{compra.n_bolsas} 🛍️</span>}
                  <button onClick={()=>descargarCompraPDF(compra)} style={{ border:'none', background:'#1976d2', color:'#fff', width:36, height:36, borderRadius:'50%', cursor:'pointer', fontSize:18, boxShadow:'0 3px 8px rgba(25,118,210,0.35)' }}>📥</button>
                </div>
              </div>
              <div style={{ fontSize:12, color:'#555', marginBottom:10, display:'flex', flexDirection:'column', gap:2, background:'#e9f3fd', borderRadius:8, padding:'6px 10px', fontWeight:600 }}>
                <span>Pedido: <strong>{compra.user_supervisora||'—'}</strong> - {formatearFecha(compra.dia_pedido)}</span>
                {compra.dia_compra && <span style={{ color:'#0d47a1' }}>Comprado: <strong>{compra.user_comprador||'—'}</strong> - {formatearFecha(compra.dia_compra)}</span>}
                {!compra.dia_compra && compra.estado === 'pedido' && <span style={{ color:'#0d47a1' }}>Compra: <strong>{compra.user_comprador||'—'}</strong></span>}
                {compra.dia_recogida && (
                  <span style={{ color:'#ca6f1aff' }}>
                    En reparto: <strong>{compra.user_repartidor||'—'}</strong> - {formatearFecha(compra.dia_recogida)}
                  </span>
                )}
                {!compra.dia_recogida && compra.estado === 'en reparto' && (
                  <span style={{ color:'#ca6f1aff' }}>
                    Preparando reparto... <strong>{compra.user_repartidor||'—'}</strong>
                  </span>
                )}
                {compra.dia_reparto && <span style={{ color:'#2d7a09' }}>Entregado: <strong>{compra.user_repartidor||'—'}</strong> - {formatearFecha(compra.dia_reparto)}</span>}
              </div>
              <details style={{ fontSize:13 }}>
                <summary
                  style={{
                    cursor: "pointer",
                    fontWeight: 700,
                    marginBottom: 6,
                    fontSize: 16,
                    color: "#1976d2",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    userSelect: "none",
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={2}
                    stroke="#1976d2"
                    style={{ width: 20, height: 20 }}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M3 14h18M7 6h10M7 18h10" />
                  </svg>
                  Productos
                </summary>
                {/* <summary style={{ cursor:'pointer', fontWeight:700, marginBottom:6, fontSize:16, color:'#1976d2', display:'flex', alignItems:'center', gap:6 }}>Productos</summary> */}
                <table style={{ width:'100%', borderCollapse:'separate', borderSpacing:'0 4px', marginTop:4, fontSize:12, color:'#444' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign:'left', padding:'6px 8px', background:'#f5f5f5' }}>Producto</th>
                      <th style={{ textAlign:'center', padding:'6px 8px', background:'#f5f5f5' }}>Cant.</th>
                      <th style={{ textAlign:'right', padding:'6px 8px', background:'#f5f5f5' }}>P. ud</th>
                      <th style={{ textAlign:'right', padding:'6px 8px', background:'#f5f5f5' }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(compra.productos||[]).map((producto, idx) => {
                      // Comprobamos si este producto fue eliminado
                      const cambioEliminado = (compra.cambios || []).find(c => c.id_producto_eliminado === producto.id);
                      if (cambioEliminado) {
                        // Si id_producto_añadido === 1 => solo tachado (eliminado sin sustitución)
                        return (
                          <tr key={idx} style={{ color: "#999" }}>
                            <td style={{ padding: "6px 12px", textDecoration: "line-through" }}>{cambioEliminado.nombre_eliminado}</td>
                            <td style={{ padding: "6px 12px", textAlign: "right", textDecoration: "line-through" }}>{cambioEliminado.cantidad_eliminado}</td>
                            <td style={{ padding: "6px 12px", textAlign: "right", textDecoration: "line-through" }}>{cambioEliminado.precio_eliminado.toFixed(2)} €</td>
                            <td style={{ textDecoration: "line-through", textAlign: "right", padding: "10px 12px", fontWeight: "700", color: "#1976d2" }}>
                              {(cambioEliminado.cantidad_eliminado * cambioEliminado.precio_eliminado).toFixed(2)} €
                            </td>
                          </tr>
                        );
                      }

                      // Comprobamos si este producto fue añadido — usar comparación normalizada para evitar fallos por acentos/espacios
                      const cambioAñadido = (compra.cambios || []).find(c => normalize(String(c.nombre_añadido)) === normalize(String(producto.nombre)));
                      if (cambioAñadido) {
                        return (
                          <tr key={idx} style={{ color: "#2e7d32" }}>
                            <td style={{ padding: "6px 12px" }}>{cambioAñadido.nombre_añadido}</td>
                            <td style={{ padding: "6px 12px", textAlign: "right" }}>{cambioAñadido.unidades_añadido}</td>
                            <td style={{ padding: "6px 12px", textAlign: "right" }}>{cambioAñadido.precio_añadido.toFixed(2)} €</td>
                            <td style={{ textAlign: "right", padding: "10px 12px", fontWeight: "700", color: "#1976d2" }}>
                              {(cambioAñadido.unidades_añadido * cambioAñadido.precio_añadido).toFixed(2)} €
                            </td>
                          </tr>
                        );
                      }

                      // Render por defecto
                      return (
                        <tr key={idx}>
                          <td style={{ padding:'4px 8px' }}>• {producto.nombre}</td>
                          <td style={{ padding:'4px 8px', textAlign:'center' }}>{producto.cantidad}</td>
                          <td style={{ padding:'4px 8px', textAlign:'right' }}>{Number(producto.precio).toFixed(2)} €</td>
                          <td style={{ padding:'4px 8px', textAlign:'right', fontWeight:600 }}>{(producto.cantidad*producto.precio).toFixed(2)} €</td>
                        </tr>
                      );
                    })}

                    {/* Render cambios que añaden productos que no están en compra.productos (added-only) */}
                    {(compra.cambios||[]).map((cambio, i) => {
                      try {
                        if (cambio.id_producto_añadido === 1) return null;
                        const exists = (compra.productos || []).some(p => normalize(String(p.nombre)) === normalize(String(cambio.nombre_añadido)));
                        if (exists) return null;
                        return (
                          <tr key={'added-'+i} style={{ color: '#2e7d32' }}>
                            <td style={{ padding: '6px 12px' }}>{cambio.nombre_añadido}</td>
                            <td style={{ padding: '6px 12px', textAlign: 'right' }}>{cambio.unidades_añadido}</td>
                            <td style={{ padding: '6px 12px', textAlign: 'right' }}>{Number(cambio.precio_añadido).toFixed(2)} €</td>
                            <td style={{ textAlign: 'right', padding: '10px 12px', fontWeight: '700', color: '#1976d2' }}>{(cambio.unidades_añadido * cambio.precio_añadido).toFixed(2)} €</td>
                          </tr>
                        );
                      } catch (_) { return null; }
                    })}
                  </tbody>
                </table>
              </details>

              {/* Desplegable de cambios */}
              {Array.isArray(compra.cambios) && compra.cambios.length > 0 && (
                <details className="cambios-details" style={{
                  marginTop: 12,
                  padding: '12px 16px',
                  background: 'linear-gradient(135deg, #fff9e6 0%, #fff3cd 100%)',
                  border: '1.5px solid #ffc107',
                  borderRadius: 10,
                  boxShadow: '0 2px 8px rgba(255,193,7,0.15)',
                }}>
                  <summary style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: '#856404',
                    marginBottom: 0,
                    letterSpacing: '0.5px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    cursor: 'pointer',
                    userSelect: 'none',
                    listStyle: 'none',
                    paddingLeft: 20,
                  }}>
                    <span style={{ fontSize: 16 }}>⚠️</span>
                    <span>Cambios realizados ({compra.cambios.length})</span>
                  </summary>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
                    {compra.cambios.map((cambio, idx) => (
                      <div key={idx} style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 4,
                        padding: '8px 12px',
                        background: '#fff',
                        borderRadius: 8,
                        border: '1px solid #e0e0e0',
                        fontSize: 12,
                      }}>
                        {cambio.id_producto_eliminado && cambio.id_producto_eliminado !== 1 && (
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                            color: '#d32f2f',
                          }}>
                            <span style={{ fontSize: 14, fontWeight: 700 }}>❌</span>
                            <span style={{ textDecoration: 'line-through', flex: 1 }}>
                              {cambio.nombre_eliminado}
                            </span>
                            <span style={{ fontWeight: 600 }}>
                              {cambio.cantidad_eliminado} ud.
                            </span>
                            <span style={{ fontWeight: 700 }}>
                              {Number(cambio.precio_eliminado || 0).toFixed(2)} €
                            </span>
                          </div>
                        )}
                        {cambio.id_producto_añadido && cambio.id_producto_añadido !== 1 && (
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                            color: '#2e7d32',
                          }}>
                            <span style={{ fontSize: 14, fontWeight: 700 }}>✅</span>
                            <span style={{ flex: 1, fontWeight: 600 }}>
                              {cambio.nombre_añadido}
                            </span>
                            <span style={{ fontWeight: 600 }}>
                              {cambio.unidades_añadido} ud.
                            </span>
                            <span style={{ fontWeight: 700 }}>
                              {Number(cambio.precio_añadido || 0).toFixed(2)} €
                            </span>
                          </div>
                        )}
                        {cambio.id_producto_añadido === 1 && (
                          <div style={{
                            fontSize: 11,
                            fontStyle: 'italic',
                            color: '#666',
                            marginTop: 2,
                          }}>
                            (No sustituido)
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </details>
              )}

              {(compra.notas_supervisora?.trim() || compra.notas_comprador?.trim()) && (
                <div style={{ marginTop:10, display:'flex', flexDirection:'column', gap:8 }}>
                  {compra.notas_supervisora?.trim() && <div style={{ background:'#f3e8ff', border:'1px solid #d8b4fe', borderRadius:8, padding:'6px 10px', color:'#6b21a8', fontSize:12 }}><strong>Notas supervisora:</strong> {compra.notas_supervisora}</div>}
                  {compra.notas_comprador?.trim() && <div style={{ background:'#e3f2fd', border:'1px solid #90caf9', borderRadius:8, padding:'6px 10px', color:'#1565c0', fontSize:12 }}><strong>Notas comprador:</strong> {compra.notas_comprador}</div>}
                </div>
              )}

              {/* Ticket area: ver/descargar/ocultar o subir ticket si no existe */}
              <div style={{ marginTop: 12, display: 'flex', justifyContent: 'center' }}>
                <div style={{ textAlign: 'center', width: '100%', maxWidth: 520, background: isPedido ? '#ccf5f1ff' : (compra.ticket ? '#ecfdf5' : '#fff1f2'), padding: 8, borderRadius: 10 }}>
                  {isPedido ? null : (compra.ticket ? (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 10 }}>
                        <button
                          onClick={() => setTicketsVisibles(p => ({ ...p, [compra.id]: !p[compra.id] }))}
                          style={{ border: 'none', background: '#1976d2', color: '#fff', padding: '10px 14px', borderRadius: 10, cursor: 'pointer', fontWeight:700 }}
                        >{ticketsVisibles[compra.id] ? 'Ocultar ticket' : 'Ver ticket'}</button>
                      </div>

                      {ticketsVisibles[compra.id] && (
                        <div style={{ borderRadius: 10, overflow: 'hidden', boxShadow: '0 6px 18px rgba(0,0,0,0.12)', marginBottom: 8 }}>
                          <AuthImage src={previewTickets[compra.id] || (compra.ticket ? `/api/tickets/${compra.ticket}` : '')} alt='ticket' style={{ width: '100%', display: 'block' }} />
                          {/* IVA breakdown (shown when available) */}
                          {(compra.iva_4 != null || compra.iva_10 != null || compra.iva_21 != null || compra.precio_comprador != null) && (
                            <div style={{ padding: 10, background: '#fafafa', borderTop: '1px solid #eee', display: 'flex', justifyContent: 'center', gap: 12, flexWrap: 'wrap' }}>
                              {compra.iva_4 != null && <div style={{ fontSize:13, color:'#333' }}>IVA 4%: <strong>{Number(compra.iva_4).toFixed(2)} €</strong></div>}
                              {compra.iva_10 != null && <div style={{ fontSize:13, color:'#333' }}>IVA 10%: <strong>{Number(compra.iva_10).toFixed(2)} €</strong></div>}
                              {compra.iva_21 != null && <div style={{ fontSize:13, color:'#333' }}>IVA 21%: <strong>{Number(compra.iva_21).toFixed(2)} €</strong></div>}
                              {compra.precio_comprador != null && <div style={{ fontSize:13, color:'#0b6623' }}>Precio total: <strong>{Number(compra.precio_comprador).toFixed(2)} €</strong></div>}
                            </div>
                          )}
                          <div style={{ display: 'flex', justifyContent: 'center', gap: 10, padding: 10, background: '#fff' }}>
                            <button
                              type='button'
                              onClick={(e) => { e.preventDefault(); descargarFicheroProtegido(`/api/tickets/${compra.ticket}`, `ticket_${compra.id}.jpg`); }}
                              style={{ background: '#fff', border: '2px solid #1976d2', color: '#1976d2', padding: '8px 12px', borderRadius: 10, textDecoration: 'none', cursor: 'pointer', fontWeight: 700 }}
                            >Descargar</button>
                            <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                              <button
                                onClick={() => openUploadModal(compra)}
                                style={{ border: 'none', background: '#f59e0b', color: '#fff', padding: '8px 12px', borderRadius: 10, cursor: 'pointer', fontWeight:700 }}
                              >Editar</button>
                            </div>
                            <button
                              onClick={() => borrarTicket(compra.id)}
                              style={{ border: 'none', background: '#d12b39', color: '#fff', padding: '8px 12px', borderRadius: 10, cursor: 'pointer', fontWeight:700 }}
                            >Eliminar</button>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div style={{ display: 'flex', justifyContent: 'center', gap: 10 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                        <button
                          onClick={() => openUploadModal(compra)}
                          style={{ border: 'none', background: '#16a34a', color: '#fff', padding: '10px 14px', borderRadius: 10, cursor: 'pointer', fontWeight:700 }}
                          disabled={uploadingForId === compra.id}
                        >{uploadingForId === compra.id ? 'Subiendo...' : 'Subir ticket'}</button>
                      </div>
                      {uploadError && uploadTargetId === compra.id && <span style={{ color: 'red', fontWeight:700 }}>{uploadError}</span>}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {helpVisible && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 12500, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.6)' }}>
          <div style={{ width: 'min(820px, 96vw)', background: '#fff', borderRadius: 12, padding: 18 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <h3 style={{ margin:0 }}>Ejemplo: cómo leer el ticket</h3>
              <button onClick={()=> {
                try {
                  if (helpPushedRef.current) { window.history.back(); return; }
                } catch(_) {}
                setHelpVisible(false);
              }} style={{ border:'none', background:'#efefef', padding:'6px 10px', borderRadius:8, cursor:'pointer' }}>Cerrar</button>
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:12, marginTop:12, alignItems:'center' }}>
              <div style={{ fontSize:13, color:'#333', maxWidth:760, textAlign:'left' }}>
                <p style={{ marginTop:0 }}>Observa en el ticket los importes de la Base Imponible para cada tipo de IVA y el precio total para rellenar el formulario.</p>
                <p style={{ margin:0 }}>Si algún IVA no está presente en el ticket, escribe 0.</p>
                <p style={{ margin:0 }}>Ejemplo: si en el ticket no aparece el IVA 10% (solo aparecen IVA 4%, 21% y el TOTAL), escribe 0 en IVA 10%.</p>
              </div>
              <img
                src='/images/info_iva_ticket.jpg'
                alt='Ejemplo ticket'
                onClick={() => {
                  try {
                    if (!helpImagePushedRef.current) {
                      window.history.pushState({ helpImage: true }, '');
                      helpImagePushedRef.current = true;
                    }
                  } catch (_) {}
                  setHelpImageVisible(true);
                }}
                style={{ width:'100%', maxWidth:760, maxHeight:420, objectFit:'contain', borderRadius:8, border:'1px solid #eee', cursor:'zoom-in' }}
                title='Pulsar para ampliar'
              />
            </div>
          </div>
        </div>
      )}

      {helpImageVisible && (
        <div
          onClick={() => {
            try {
              if (helpImagePushedRef.current) {
                window.history.back(); // will trigger popstate and close
                return;
              }
            } catch (_) {}
            setHelpImageVisible(false);
          }}
          style={{ position: 'fixed', inset: 0, zIndex: 13000, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.85)' }}
        >
          <div onClick={(e) => e.stopPropagation()} style={{ position: 'relative', maxWidth: '98vw', maxHeight: '98vh' }}>
            <img src='/images/info_iva_ticket.jpg' alt='Ejemplo ticket ampliado' style={{ width: '100%', height: 'auto', maxHeight: '95vh', objectFit: 'contain', borderRadius: 8 }} />
            <button
              onClick={() => {
                try {
                  if (helpImagePushedRef.current) {
                    window.history.back();
                    return;
                  }
                } catch (_) {}
                setHelpImageVisible(false);
              }}
              style={{ position: 'absolute', top: 10, right: 10, background: 'rgba(255,255,255,0.9)', border: 'none', padding: '6px 10px', borderRadius: 6, cursor: 'pointer', fontWeight:700 }}
            >Cerrar</button>
          </div>
        </div>
      )}

      {total > pageSize && (
        <nav aria-label='Paginación de compras' style={{ position:'fixed', left:0, right:0, bottom:0, zIndex:999, backdropFilter:'blur(6px)', background:'rgba(255,255,255,0.88)', borderTop:'1px solid #d0d7de', boxShadow:'0 -2px 6px rgba(0,0,0,0.08)', padding:'10px 12px' }}>
          <div style={{ maxWidth:'1100px', margin:'0 auto', display:'flex', flexDirection:'column', gap:6 }}>
            <div style={{ display:'flex', flexWrap:'wrap', gap:6, alignItems:'center', justifyContent:'center' }}>
              {/* Toggle filtros removido de la barra inferior */}
              <button aria-label='Primera página' disabled={page===1} onClick={()=> goToPage(1)}>⏮</button>
              <button aria-label='Anterior' disabled={page===1} onClick={()=> goToPage(page-1)}>◀</button>
              {pageWindow.map((p,i)=> p==='…' ? (
                <span key={'elip'+i} style={{ padding:'6px 10px', fontWeight:700 }}>…</span>
              ) : (
                <button
                  key={p}
                  onClick={()=> goToPage(p)}
                  aria-current={p===page ? 'page': undefined}
                  style={{
                    padding:'6px 12px',
                    fontWeight:700,
                    border:'2px solid #1976d2',
                    background: p===page? '#1976d2':'#fff',
                    color: p===page? '#fff':'#1976d2',
                    borderRadius:8,
                    minWidth:44
                  }}
                >{p}</button>
              ))}
              <button aria-label='Siguiente' disabled={page===totalPages} onClick={()=> goToPage(page+1)}>▶</button>
              <button aria-label='Última página' disabled={page===totalPages} onClick={()=> goToPage(totalPages)}>⏭</button>
            </div>
            <div style={{ fontWeight:700, fontSize:13, color:'#224', textAlign:'center' }}>Página {page} / {totalPages} · {total} compras</div>
          </div>
        </nav>
      )}
      {loading && <p style={{ textAlign:'center', marginTop:8 }}>Cargando...</p>}
    </div>
  );
};

export default VerComprasComprador;